import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { RootStackParamList } from '../../types/navigation';
import { useSubscription } from '../../hooks/useSubscription';
import { useAccessibility } from '../../hooks/useAccessibility';
import AccessibleText from '../../components/common/AccessibleText';
import i18n from '../../i18n';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';
import { palette } from '../../utils/palette';

const THEME_COLOR = palette.softViolet;

const PremiumPaywallScreen = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { upgradeSubscription, restorePurchases } = useSubscription();
  useAccessibility();
  const { data: pricingFlag, isLoading: isLoadingFlag } = useFeatureFlag(
    'pricing_subscription'
  );

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>(
    'yearly'
  );
  const [isProcessing, setIsProcessing] = useState(false);

  // Redirect if feature flag is enabled (hide paywall when flag is enabled)
  useEffect(() => {
    if (
      !isLoadingFlag &&
      pricingFlag &&
      isFeatureEnabled(pricingFlag, 'pricing_subscription')
    ) {
      // Feature flag is enabled, hide paywall - go back
      navigation.goBack();
    }
  }, [pricingFlag, isLoadingFlag, navigation]);

  // Show loading while checking feature flag
  if (isLoadingFlag) {
    return (
      <View
        style={[
          styles.container,
          { justifyContent: 'center', alignItems: 'center' },
        ]}
      >
        <ActivityIndicator size="large" color={THEME_COLOR} />
      </View>
    );
  }

  // Hide screen if feature flag is enabled
  if (isFeatureEnabled(pricingFlag, 'pricing_subscription')) {
    return null;
  }

  const handleUpgrade = async () => {
    setIsProcessing(true);

    try {
      await upgradeSubscription();
      navigation.goBack();
    } catch {
      // Upgrade failed
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRestore = async () => {
    setIsProcessing(true);

    try {
      await restorePurchases();
      navigation.goBack();
    } catch {
      // Restore failed
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.closeButton}
          onPress={() => navigation.goBack()}
          accessibilityLabel={i18n.t('common.cancel')}
          accessibilityRole="button"
        >
          <Ionicons name="close" size={24} color="#6B7280" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          <View style={styles.bannerContainer}>
            <Ionicons name="star" size={80} color={THEME_COLOR} />
            <Ionicons
              name="star-outline"
              size={60}
              color="#FFD700"
              style={styles.sparkleIcon}
            />
          </View>

          <AccessibleText variant="h1" style={styles.title}>
            {i18n.t('premium.title')}
          </AccessibleText>

          <AccessibleText variant="body" style={styles.subtitle}>
            {i18n.t('premium.subtitle')}
          </AccessibleText>

          {/* Feature List */}
          <View style={styles.featureList}>
            <FeatureItem
              icon="shirt-outline"
              title={i18n.t('premium.smartWardrobe')}
              description={i18n.t('premium.wardrobeDesc')}
            />

            <FeatureItem
              icon="sunny-outline"
              title={i18n.t('premium.wearToday')}
              description={i18n.t('premium.wearTodayDesc')}
            />

            <FeatureItem
              icon="infinite-outline"
              title={i18n.t('premium.unlimited')}
              description={i18n.t('premium.unlimitedDesc')}
            />

            <FeatureItem
              icon="shield-checkmark-outline"
              title={i18n.t('premium.noAds')}
              description={i18n.t('premium.noAdsDesc')}
            />
          </View>

          {/* Subscription Plans */}
          <View style={styles.planSelector}>
            <TouchableOpacity
              style={[
                styles.planOption,
                selectedPlan === 'monthly' && styles.selectedPlan,
              ]}
              onPress={() => setSelectedPlan('monthly')}
              accessibilityRole="radio"
              accessibilityState={{ checked: selectedPlan === 'monthly' }}
              accessibilityLabel={i18n.t('premium.monthly')}
            >
              <AccessibleText
                variant="h3"
                style={[
                  styles.planTitle,
                  selectedPlan === 'monthly' ? styles.selectedPlanText : {},
                ]}
              >
                {i18n.t('premium.monthly')}
              </AccessibleText>

              <AccessibleText
                variant="h2"
                style={[
                  styles.planPrice,
                  selectedPlan === 'monthly' ? styles.selectedPlanText : {},
                ]}
              >
                $4.99
              </AccessibleText>

              <AccessibleText
                variant="caption"
                style={[
                  styles.planInterval,
                  selectedPlan === 'monthly' ? styles.selectedPlanText : {},
                ]}
              >
                {i18n.t('premium.perMonth')}
              </AccessibleText>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.planOption,
                selectedPlan === 'yearly' && styles.selectedPlan,
                styles.yearlyPlan,
              ]}
              onPress={() => setSelectedPlan('yearly')}
              accessibilityRole="radio"
              accessibilityState={{ checked: selectedPlan === 'yearly' }}
              accessibilityLabel={i18n.t('premium.yearly')}
            >
              <View style={styles.saveTag}>
                <AccessibleText variant="caption" style={styles.saveTagText}>
                  {i18n.t('premium.save')}
                </AccessibleText>
              </View>

              <AccessibleText
                variant="h3"
                style={[
                  styles.planTitle,
                  selectedPlan === 'yearly' ? styles.selectedPlanText : {},
                ]}
              >
                {i18n.t('premium.yearly')}
              </AccessibleText>

              <AccessibleText
                variant="h2"
                style={[
                  styles.planPrice,
                  selectedPlan === 'yearly' ? styles.selectedPlanText : {},
                ]}
              >
                $39.99
              </AccessibleText>

              <AccessibleText
                variant="caption"
                style={[
                  styles.planInterval,
                  selectedPlan === 'yearly' ? styles.selectedPlanText : {},
                ]}
              >
                {i18n.t('premium.perYear')}
              </AccessibleText>
            </TouchableOpacity>
          </View>

          {/* Upgrade Button */}
          <TouchableOpacity
            style={styles.upgradeButton}
            onPress={handleUpgrade}
            disabled={isProcessing}
            accessibilityRole="button"
            accessibilityLabel={i18n.t('premium.upgradeButton')}
            accessibilityState={{ disabled: isProcessing }}
          >
            {isProcessing ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <AccessibleText variant="button" style={styles.upgradeButtonText}>
                {i18n.t('premium.upgradeButton')}
              </AccessibleText>
            )}
          </TouchableOpacity>

          {/* Restore Purchases */}
          <TouchableOpacity
            style={styles.restoreButton}
            onPress={handleRestore}
            disabled={isProcessing}
            accessibilityRole="button"
            accessibilityLabel={i18n.t('premium.restore')}
            accessibilityState={{ disabled: isProcessing }}
          >
            <AccessibleText variant="button" style={styles.restoreButtonText}>
              {i18n.t('premium.restore')}
            </AccessibleText>
          </TouchableOpacity>

          {/* Terms */}
          <AccessibleText variant="caption" style={styles.termsText}>
            {i18n.t('premium.terms')}
          </AccessibleText>
        </View>
      </ScrollView>
    </View>
  );
};

interface FeatureItemProps {
  icon: string;
  title: string;
  description: string;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  icon,
  title,
  description,
}) => {
  return (
    <View style={styles.featureItem}>
      <View style={styles.featureIconContainer}>
        <Ionicons
          name={icon as keyof typeof Ionicons.glyphMap}
          size={24}
          color={THEME_COLOR}
        />
      </View>
      <View style={styles.featureContent}>
        <AccessibleText variant="h3" style={styles.featureTitle}>
          {title}
        </AccessibleText>
        <AccessibleText variant="body" style={styles.featureDescription}>
          {description}
        </AccessibleText>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: palette.white,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: 16,
    paddingTop: 60,
    paddingBottom: 16,
  },
  closeButton: {
    padding: 8,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    paddingBottom: 40,
  },
  bannerContainer: {
    width: '100%',
    height: 200,
    marginBottom: 24,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  sparkleIcon: {
    position: 'absolute',
    top: 40,
    right: '35%',
  },
  title: {
    textAlign: 'center',
    marginBottom: 8,
    color: THEME_COLOR,
  },
  subtitle: {
    textAlign: 'center',
    marginBottom: 32,
    paddingHorizontal: 16,
    color: palette.darkGrayBlue,
  },
  featureList: {
    marginBottom: 32,
  },
  featureItem: {
    flexDirection: 'row',
    marginBottom: 16,
    alignItems: 'flex-start',
  },
  featureIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(148, 117, 225, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  featureContent: {
    flex: 1,
  },
  featureTitle: {
    marginBottom: 4,
  },
  featureDescription: {
    color: palette.darkGrayBlue,
  },
  planSelector: {
    flexDirection: 'row',
    marginBottom: 24,
  },
  planOption: {
    flex: 1,
    borderWidth: 2,
    borderColor: palette.lightGray,
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 6,
    alignItems: 'center',
  },
  selectedPlan: {
    borderColor: THEME_COLOR,
    backgroundColor: 'rgba(148, 117, 225, 0.05)',
  },
  yearlyPlan: {
    position: 'relative',
  },
  saveTag: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: palette.cyanLimeGreen,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  saveTagText: {
    color: palette.white,
    fontWeight: 'bold',
  },
  planTitle: {
    marginBottom: 8,
  },
  planPrice: {
    marginBottom: 4,
  },
  planInterval: {
    color: palette.darkGrayBlue,
  },
  selectedPlanText: {
    color: THEME_COLOR,
  },
  upgradeButton: {
    backgroundColor: THEME_COLOR,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginBottom: 16,
  },
  upgradeButtonText: {
    color: palette.white,
  },
  restoreButton: {
    paddingVertical: 16,
    alignItems: 'center',
    marginBottom: 24,
  },
  restoreButtonText: {
    color: THEME_COLOR,
  },
  termsText: {
    textAlign: 'center',
    color: palette.mediumGray,
    paddingHorizontal: 24,
  },
});

export default PremiumPaywallScreen;
